import { supabase } from '../lib/supabase';
import type { TrophyType } from '../types/trophy';

// Event bus for trophy notifications
const trophyEventBus = new EventTarget();

export function subscribeTrophyNotifications(callback: (trophies: TrophyType[]) => void) {
  const handler = (event: Event) => {
    if (event instanceof CustomEvent) {
      callback(event.detail);
    }
  };
  trophyEventBus.addEventListener('trophyEarned', handler);
  return () => trophyEventBus.removeEventListener('trophyEarned', handler);
}

export async function checkAndAwardTrophies(userId: string) {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // Get user's current trophies
    const { data: existingTrophies, error: trophyError } = await supabase
      .from('user_trophies')
      .select('type')
      .eq('user_id', userId);

    if (trophyError) throw trophyError;

    const earnedTypes = new Set(existingTrophies?.map(t => t.type));
    const newTrophies: TrophyType[] = [];

    // Check for first comment trophy
    const { count: commentCount, error: commentError } = await supabase
      .from('forum_comments')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (commentError) throw commentError;

    if (commentCount && commentCount > 0 && !earnedTypes.has('first_comment')) {
      newTrophies.push('first_comment');
    }

    // Award new trophies
    if (newTrophies.length > 0) {
      const { error: insertError } = await supabase
        .from('user_trophies')
        .insert(newTrophies.map(type => ({
          user_id: userId,
          type
        })));

      if (insertError) throw insertError;

      // Dispatch trophy notification event
      trophyEventBus.dispatchEvent(new CustomEvent('trophyEarned', {
        detail: newTrophies
      }));
    }

    return newTrophies;
  } catch (error) {
    console.error('Error checking/awarding trophies:', error);
    throw error;
  }
}